import { BONES, KEYPOINTS, bubbleAt, poseBox, poseOf, type Keypoint, type StageState } from './poseModel'

/**
 * Draws one frame of the pose stage. Two looks over the same points:
 *
 * - `scene` — the overlay a pose model draws on a live picture: white bones,
 *   coloured joints, a soft glow, the game's bubbles in full colour.
 * - `camera` — what Kalqy keeps once the picture is gone: the same seventeen
 *   points, the detector's box and the numbers the game reads, nothing else.
 */

export type StageLook = 'scene' | 'camera'

const JOINT_HUE: Record<Keypoint, string> = {
  nose: '#f5b301',
  leftEye: '#f5b301',
  rightEye: '#f5b301',
  leftEar: '#f5b301',
  rightEar: '#f5b301',
  leftShoulder: '#ff017b',
  leftElbow: '#ff017b',
  leftWrist: '#ff017b',
  rightShoulder: '#00a9a2',
  rightElbow: '#00a9a2',
  rightWrist: '#00a9a2',
  leftHip: '#2e90d9',
  leftKnee: '#2e90d9',
  leftAnkle: '#2e90d9',
  rightHip: '#45b308',
  rightKnee: '#45b308',
  rightAnkle: '#45b308',
}

const FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif"
const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"

export function drawStage(ctx: CanvasRenderingContext2D, s: StageState, look: StageLook): void {
  const { w, h, size, bubbleR } = s.layout
  ctx.clearRect(0, 0, w, h)
  const camera = look === 'camera'

  /* ── Bubbles ── */
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  for (const b of s.bubbles) {
    if (b.popped) continue
    const at = bubbleAt(b, s.t)
    const dim = b.wobble > 0 ? 0.45 : 1
    ctx.save()
    ctx.globalAlpha = dim
    ctx.beginPath()
    ctx.arc(at.x, at.y, bubbleR, 0, Math.PI * 2)
    if (camera) {
      ctx.fillStyle = 'rgba(255,255,255,0.06)'
      ctx.fill()
      ctx.setLineDash([4, 4])
      ctx.lineWidth = 1.5
      ctx.strokeStyle = 'rgba(255,255,255,0.55)'
      ctx.stroke()
      ctx.setLineDash([])
    } else {
      ctx.shadowColor = b.hue
      ctx.shadowBlur = 22
      ctx.fillStyle = 'rgba(255,255,255,0.94)'
      ctx.fill()
      ctx.shadowBlur = 0
      ctx.lineWidth = 3.5
      ctx.strokeStyle = b.hue
      ctx.stroke()
      // A highlight, so it reads as a bubble rather than a disc.
      ctx.beginPath()
      ctx.arc(at.x - bubbleR * 0.38, at.y - bubbleR * 0.42, bubbleR * 0.18, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(255,255,255,0.9)'
      ctx.fill()
    }
    ctx.font = `700 ${String(Math.round(bubbleR * 1.0))}px ${FONT}`
    ctx.fillStyle = camera ? 'rgba(255,255,255,0.85)' : b.hue
    ctx.fillText(b.label, at.x, at.y + bubbleR * 0.04)
    ctx.restore()
  }

  const pose = poseOf(s)
  const bone = Math.max(2.5, size * 0.014)
  const joint = Math.max(3.5, size * 0.017)

  /* ── The reaching wrist's trail ── */
  if (s.trail.length > 2) {
    const hue = s.side < 0 ? JOINT_HUE.leftWrist : JOINT_HUE.rightWrist
    ctx.save()
    ctx.lineCap = 'round'
    for (let i = 1; i < s.trail.length; i++) {
      const a = s.trail[i - 1]
      const b = s.trail[i]
      if (!a || !b) continue
      const u = i / s.trail.length
      ctx.globalAlpha = u * 0.55
      ctx.strokeStyle = hue
      ctx.lineWidth = bone * (0.6 + u * 1.6)
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
    }
    ctx.restore()
  }

  /* ── The detector's box (camera look) ── */
  if (camera) {
    const box = poseBox(pose, size * 0.06)
    const c = Math.min(box.w, box.h) * 0.14
    ctx.save()
    ctx.strokeStyle = '#f5b301'
    ctx.lineWidth = 2
    ctx.beginPath()
    for (const [x, y, dx, dy] of [
      [box.x, box.y, 1, 1],
      [box.x + box.w, box.y, -1, 1],
      [box.x, box.y + box.h, 1, -1],
      [box.x + box.w, box.y + box.h, -1, -1],
    ] as const) {
      ctx.moveTo(x + dx * c, y)
      ctx.lineTo(x, y)
      ctx.lineTo(x, y + dy * c)
    }
    ctx.stroke()
    ctx.font = `600 11px ${FONT}`
    const tag = 'child · 0.98'
    const tw = ctx.measureText(tag).width + 12
    ctx.fillStyle = '#f5b301'
    ctx.fillRect(box.x, box.y - 20, tw, 18)
    ctx.fillStyle = '#141413'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText(tag, box.x + 6, box.y - 11)
    ctx.restore()
  }

  /* ── Head ring (scene look only — the camera keeps the five face points, not a head) ── */
  if (!camera) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(pose.nose.x, pose.nose.y - size * 0.026, size * 0.1, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(255,255,255,0.4)'
    ctx.lineWidth = Math.max(1.5, bone * 0.7)
    ctx.stroke()
    ctx.restore()
  }

  /* ── Bones ── */
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = camera ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.95)'
  ctx.shadowColor = 'rgba(255,255,255,0.7)'
  ctx.shadowBlur = camera ? 6 : 12
  ctx.lineWidth = bone
  ctx.beginPath()
  for (const [a, b] of BONES) {
    ctx.moveTo(pose[a].x, pose[a].y)
    ctx.lineTo(pose[b].x, pose[b].y)
  }
  ctx.stroke()
  ctx.restore()

  /* ── Joints ── */
  ctx.save()
  for (const k of KEYPOINTS) {
    const p = pose[k]
    const face = k === 'nose' || k.endsWith('Eye') || k.endsWith('Ear')
    const r = face ? joint * 0.8 : joint
    ctx.beginPath()
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
    ctx.fillStyle = JOINT_HUE[k]
    ctx.shadowColor = JOINT_HUE[k]
    ctx.shadowBlur = 10
    ctx.fill()
    ctx.shadowBlur = 0
    ctx.lineWidth = 1.5
    ctx.strokeStyle = '#ffffff'
    ctx.stroke()
  }
  ctx.restore()

  /* ── The numbers the game reads (camera look) ── */
  if (camera) {
    ctx.save()
    ctx.font = `500 10.5px ${MONO}`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    for (const k of ['leftWrist', 'rightWrist', 'leftAnkle', 'rightAnkle'] as const) {
      const p = pose[k]
      const label = `${(p.x / w).toFixed(2)}, ${(p.y / h).toFixed(2)}`
      const side = k.startsWith('left') ? -1 : 1
      const tw = ctx.measureText(label).width
      const x = side < 0 ? p.x - tw - 12 : p.x + 12
      ctx.fillStyle = 'rgba(10,10,12,0.55)'
      ctx.fillRect(x - 4, p.y - 8, tw + 8, 16)
      ctx.fillStyle = 'rgba(255,255,255,0.88)'
      ctx.fillText(label, x, p.y)
    }
    ctx.restore()
  }

  /* ── Confetti ── */
  ctx.save()
  for (const p of s.particles) {
    ctx.globalAlpha = Math.min(1, p.life * 1.8)
    ctx.fillStyle = p.hue
    ctx.beginPath()
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}
